import React, { useState, useEffect, useMemo } from 'react';
import { Film, Image as ImageIcon, Paperclip } from 'lucide-react';
import './MediaUploader.css';

/**
 * Reusable MediaUploader component that allows uploading via URL or file
 * @param {string} label - Label for the input (e.g., "Video", "Thumbnail")
 * @param {string} type - Media type: 'video' or 'image'
 * @param {string} contentUrl - Current URL value
 * @param {File} contentFile - Current file object
 * @param {boolean} isUploading - Whether currently uploading
 * @param {boolean} isUploaded - Whether upload is complete
 * @param {Function} onUrlChange - Callback when URL changes
 * @param {Function} onFileChange - Callback when file changes
 * @param {string} uploadMode - Current mode: 'url' or 'file' (for tracking which was last used)
 */
function MediaUploader({
    label,
    type = 'video', // 'video' or 'image'
    contentUrl = '',
    contentFile = null,
    isUploading = false,
    isUploaded = false,
    onUrlChange = () => {},
    onFileChange = () => {},
    uploadMode = 'url'
}) {
    const [mode, setMode] = useState(uploadMode || 'url');

    // Auto-switch to URL mode when a URL arrives (e.g. prefilled from analysis) and no file is selected
    useEffect(() => {
        if (contentUrl && !contentFile) {
            setMode('url');
        }
    }, [contentUrl, contentFile]);

    // Create an object URL for file previews (revoked on cleanup to prevent memory leaks)
    const filePreviewUrl = useMemo(() => {
        if (contentFile) {
            return URL.createObjectURL(contentFile);
        }
        return null;
    }, [contentFile]);

    useEffect(() => {
        return () => {
            if (filePreviewUrl) {
                URL.revokeObjectURL(filePreviewUrl);
            }
        };
    }, [filePreviewUrl]);

    const acceptTypes = type === 'image' ? 'image/*' : 'video/*';
    const placeholderText = type === 'image' ? 'https://example.com/image.jpg' : 'https://example.com/video.mp4';
    const fileInputId = `${label.toLowerCase().replace(/\s+/g, '-')}-file-input`;
    const urlInputId = `${label.toLowerCase().replace(/\s+/g, '-')}-url-input`;

    const handleModeChange = (newMode) => {
        setMode(newMode);
        if (newMode === 'url' && contentFile) {
            // Clear file when switching to URL mode
            onFileChange(null);
        } else if (newMode === 'file' && contentUrl) {
            // Clear URL when switching to file mode
            onUrlChange('');
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            onFileChange(file);
        }
    };

    const handleUrlChange = (e) => {
        const url = e.target.value;
        onUrlChange(url);
    };

    const handleChangeMedia = () => {
        if (mode === 'file') {
            document.getElementById(fileInputId)?.click();
        }
    };

    const getStatusIndicator = () => {
        if (isUploading) {
            return <span className="media-status uploading">Uploading…</span>;
        }
        if (isUploaded) {
            return <span className="media-status uploaded">Uploaded</span>;
        }
        if (contentFile || contentUrl) {
            return <span className="media-status ready">Ready</span>;
        }
        return null;
    };

    return (
        <div className="media-uploader">
            <div className="media-header">
                <label>{label}</label>
                <div className="media-mode-toggle">
                    <button
                        type="button"
                        className={`mode-btn ${mode === 'url' ? 'active' : ''}`}
                        onClick={() => handleModeChange('url')}
                    >
                        URL
                    </button>
                    <button
                        type="button"
                        className={`mode-btn ${mode === 'file' ? 'active' : ''}`}
                        onClick={() => handleModeChange('file')}
                    >
                        Upload
                    </button>
                </div>
            </div>

            <div className="media-input-container">
                {mode === 'url' ? (
                    <>
                        <input
                            type="url"
                            id={urlInputId}
                            value={contentUrl}
                            onChange={handleUrlChange}
                            placeholder={placeholderText}
                            className="media-url-input"
                        />
                        {contentUrl && (
                            <div className="media-preview">
                                {type === 'image' ? (
                                    <img
                                        src={contentUrl}
                                        alt={label}
                                        className="media-preview-image"
                                    />
                                ) : (
                                    <video
                                        src={contentUrl}
                                        className="media-preview-video"
                                        controls
                                        preload="metadata"
                                    />
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {contentFile ? (
                            <div className="file-selected-box">
                                <div className="file-info">
                                    <span className="file-icon"><Paperclip size={16} aria-hidden="true" /></span>
                                    <div className="file-details">
                                        <span className="file-name">{contentFile.name}</span>
                                        <span className="file-size">
                                            {(contentFile.size / 1024 / 1024).toFixed(2)} MB
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="change-btn"
                                    onClick={handleChangeMedia}
                                >
                                    Change
                                </button>
                                {filePreviewUrl && (
                                    <div className="media-preview">
                                        {type === 'image' ? (
                                            <img
                                                src={filePreviewUrl}
                                                alt={label}
                                                className="media-preview-image"
                                            />
                                        ) : (
                                            <video
                                                src={filePreviewUrl}
                                                className="media-preview-video"
                                                controls
                                                preload="metadata"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div
                                className="file-upload-area"
                                onClick={() => document.getElementById(fileInputId)?.click()}
                            >
                                <span className="upload-icon">
                                    {type === 'image' ? <ImageIcon size={28} aria-hidden="true" /> : <Film size={28} aria-hidden="true" />}
                                </span>
                                <p>Click to upload {label.toLowerCase()}</p>
                                <span className="file-type">
                                    {type === 'image'
                                        ? 'JPG, PNG, GIF, WebP'
                                        : 'MP4, WebM, MOV, AVI'}
                                </span>
                            </div>
                        )}
                        <input
                            ref={(ref) => {
                                window[fileInputId] = ref;
                            }}
                            type="file"
                            id={fileInputId}
                            accept={acceptTypes}
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                    </>
                )}
            </div>

            {getStatusIndicator()}
        </div>
    );
}

export default MediaUploader;
